import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Search, Plus, RefreshCw, ChevronRight } from 'lucide-react';
import { apiClient, formatDate, Button, Input, Spinner } from '@dukene/shared';
import { useNavigate } from 'react-router-dom';

interface EmailRecord {
    id: string;
    resendId: string | null;
    from: string;
    to: string;
    subject: string;
    body: string | null;
    html: string | null;
    status: 'SENT' | 'DELIVERED' | 'OPENED' | 'CLICKED' | 'BOUNCED' | 'COMPLAINED' | 'FAILED';
    direction: 'INBOUND' | 'OUTBOUND';
    createdAt: string;
    metadata: any;
}

export default function Email() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
    const [searchQuery, setSearchQuery] = useState('');

    const { data: emails, isLoading, refetch, isFetching } = useQuery<EmailRecord[]>({
        queryKey: ['emails-history'],
        queryFn: async () => {
            return apiClient.get<EmailRecord[]>('/emails/history');
        },
    });

    const filteredEmails = (emails || []).filter(email => {
        const matchesTab = email.direction === activeTab;
        const matchesSearch =
            email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.to.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.from.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesTab && matchesSearch;
    });

    const getStatusBadge = (status: EmailRecord['status']) => {
        switch (status) {
            case 'SENT': return <div className="w-2 h-2 rounded-full bg-blue-500" title="Sent" />;
            case 'DELIVERED': return <div className="w-2 h-2 rounded-full bg-emerald-500" title="Delivered" />;
            case 'OPENED': return <div className="w-2 h-2 rounded-full bg-violet-500" title="Opened" />;
            case 'CLICKED': return <div className="w-2 h-2 rounded-full bg-indigo-500" title="Clicked" />;
            case 'FAILED': return <div className="w-2 h-2 rounded-full bg-rose-500" title="Failed" />;
            case 'BOUNCED': return <div className="w-2 h-2 rounded-full bg-orange-500" title="Bounced" />;
            default: return <div className="w-2 h-2 rounded-full bg-gray-300" />;
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6 pb-12 px-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-white shadow-xl shadow-black/10">
                        <Mail className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black tracking-tight text-black">Messages</h1>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            {filteredEmails.length} Messages total
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => refetch()}
                        variant="secondary"
                        size="sm"
                        className="rounded-xl h-10 w-10 p-0 border-gray-100 bg-white"
                        disabled={isFetching}
                    >
                        <RefreshCw className={`w-4 h-4 text-gray-400 ${isFetching ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                        onClick={() => navigate('/email/compose')}
                        className="bg-black text-white hover:bg-gray-800 rounded-xl px-6 h-10 flex items-center gap-2 font-black text-xs transition-all shadow-lg shadow-black/5"
                    >
                        <Plus className="w-4 h-4" />
                        Compose Email
                    </Button>
                </div>
            </div>

            {/* Search & Tabs */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex p-1 bg-gray-100 rounded-xl border border-gray-200/50">
                    <button
                        onClick={() => setActiveTab('INBOUND')}
                        className={`px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'INBOUND' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
                    >
                        Inbox
                    </button>
                    <button
                        onClick={() => setActiveTab('OUTBOUND')}
                        className={`px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'OUTBOUND' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
                    >
                        Sent
                    </button>
                </div>
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input
                        placeholder="Search communications..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 h-10 rounded-xl border-gray-100 bg-white text-xs font-medium focus:ring-1 focus:ring-black transition-all"
                    />
                </div>
            </div>

            {/* Inbox List */}
            <div className="bg-white border border-gray-100 rounded-[24px] overflow-hidden shadow-2xl shadow-black/[0.02]">
                {isLoading ? (
                    <div className="p-20 flex flex-col items-center justify-center gap-3">
                        <Spinner className="w-8 h-8 text-black border-2" />
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Inbox...</p>
                    </div>
                ) : filteredEmails.length === 0 ? (
                    <div className="p-20 text-center space-y-4">
                        <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto border border-gray-100">
                            <Mail className="w-8 h-8 text-gray-200" />
                        </div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No Messages Found</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50/50">
                        {/* Header Row */}
                        <div className="hidden md:flex items-center px-6 py-3 bg-gray-50/50 border-b border-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
                            <div className="w-8">Stat</div>
                            <div className="w-1/3">Subject</div>
                            <div className="w-1/4">{activeTab === 'INBOUND' ? 'Sender' : 'Recipient'}</div>
                            <div className="flex-1 text-right">Date & Time</div>
                        </div>

                        {filteredEmails.map((email) => (
                            <div
                                key={email.id}
                                onClick={() => navigate(`/email/${email.id}`)}
                                className="group flex flex-col md:flex-row md:items-center px-6 py-4 hover:bg-gray-50 transition-all cursor-pointer gap-2 md:gap-0"
                            >
                                <div className="hidden md:flex w-8 items-center">
                                    {getStatusBadge(email.status)}
                                </div>

                                <div className="w-full md:w-1/3 min-w-0 pr-4">
                                    <h4 className="font-extrabold text-gray-900 truncate text-sm group-hover:text-black transition-colors">
                                        {email.subject || '(No Subject)'}
                                    </h4>
                                    <div className="md:hidden flex items-center gap-2 mt-1">
                                        {getStatusBadge(email.status)}
                                        <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest leading-none">
                                            {email.status}
                                        </span>
                                    </div>
                                </div>

                                <div className="w-full md:w-1/4 min-w-0">
                                    <p className="text-xs font-bold text-gray-500 truncate group-hover:text-gray-900 transition-colors">
                                        {activeTab === 'OUTBOUND' ? email.to : email.from}
                                    </p>
                                </div>

                                <div className="flex-1 flex items-center justify-between md:justify-end gap-6 text-right">
                                    <p className="text-[10px] font-black text-gray-400 group-hover:text-gray-900 transition-colors">
                                        {formatDate(email.createdAt)}
                                    </p>
                                    <div className="w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-black group-hover:text-white transition-all opacity-0 group-hover:opacity-100">
                                        <ChevronRight className="w-4 h-4" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex justify-center pt-4">
                <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">
                    Showing {filteredEmails.length} messages recorded
                </p>
            </div>
        </div>
    );
}
